import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { AccessControlServiceConfig, AccessGroup, AccessRelationship, AccessTuple, ObjectRelation } from './access-control.service.types';


@Injectable({
  providedIn: 'root',
})

export class AccessControlService {
  constructor(
    private httpClient: HttpClient,
    @Inject('accessControlServiceConfig') private accessControlServiceConfig: AccessControlServiceConfig
  ) {}

  // Create default groups for assigning access in the drop down, one for Jax and one for all users
  // The user value represents the user syntax required for constructing the tuple
  // Since these will not have email addresses to use for display, set labels and initials  
  // TODO: Push this to FGA library so the UI doesn't need to know the syntax
  
  groups: AccessGroup[] = [    
    {user: "*", label: "All BioConnect Users", initials: "ALL", user_type: "user"},
    {user: "JAX#member", label: "All Users at The Jackson Laboratory", initials: "JAX", user_type: "institution"},
  ]

  private restServer = this.accessControlServiceConfig.restServer;

  getTuplesForObject(objectType: string, objectId: number): Observable<AccessTuple[]> {
    return this.httpClient.get<AccessTuple[]>(
      `${this.restServer}/authorization/get_tuples_for_object/?object_type=${objectType}&object_id=${objectId}`
    ).pipe(map((tuples) => {
      
      const sortedGroups: AccessTuple[] = [];
      const sortedUsers: AccessTuple[] = [];

      // Create a sorted list of groups
      this.groups.forEach(group => {
        sortedGroups.push(...tuples.filter(t => group.user === t.user));
      });
      tuples = tuples.filter(function(tuple) {
        return sortedGroups.indexOf(tuple) < 0;
      } );

      // Create a sorted list of users
      // Re-order users by relationship type, need to keep the order they are defined in
      const relationships = Object.keys(AccessRelationship)
      tuples.filter(t => t.user_type==='user');
      relationships.forEach((relation) => {
        sortedUsers.push(...tuples.filter(t => t.relation === relation));
      });

      return [...sortedGroups, ...sortedUsers];
    }));
  }

  getObjectList(user: string, relation: string, objectType: string) {
    return this.httpClient.get<string[]>(
      `${this.restServer}/authorization/get_object_list/?user=${user}&relation=${relation}&object_type=${objectType}`
    );
  }

  addTuple(user: string, user_type: string, relation: string, objectType: string, objectId: number): Observable<boolean> {
    const newTuple: AccessTuple = {
      user: user, 
      user_type: user_type,
      relation: relation, 
      object_type: objectType, 
      object_id: objectId
    }
    return this.httpClient.post(
      `${this.restServer}/authorization/add_tuple/`,
      newTuple
    ).pipe(map((result) => <boolean>result));
  }

  updateTuple(user: string, user_type: string, relation: string, objectType: string, objectId: number): Observable<boolean> {
    const tuple: AccessTuple = {
      user: user, 
      user_type: user_type,
      relation: relation, 
      object_type: objectType, 
      object_id: objectId
    }
    return this.httpClient.patch(
      `${this.restServer}/authorization/update_tuple_relation/`,
      tuple
    ).pipe(map((result) => <boolean>result));
  }

  deleteTuple(user: string, user_type: string, relation: string, objectType: string, objectId: number): Observable<boolean> {
    const encodedUserName = encodeURIComponent(user); // user may contain hash symbol (eg JAX#member)
    return this.httpClient.delete(
      `${this.restServer}/authorization/delete_tuple/?user_id=${encodedUserName}&relation=${relation}&object_type=${objectType}&object_id=${objectId}&user_type=${user_type}`
    ).pipe(map((result) => <boolean>result));
  }

  getObjectListRelations(user: string, objectType: string, objectIds: number[]): Observable<ObjectRelation[]> {
      return this.httpClient.post<ObjectRelation[]>(
        `${this.restServer}/authorization/get_object_list_relations/`,
        {user: user, object_type: objectType, object_ids: objectIds}
      );
  }

  checkPermissions(relation: string, objectType: string, objectId: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${this.restServer}/authorization/check_permissions/?relation=${relation}&object_type=${objectType}&object_id=${objectId}`
    );
  }
  
}
